<!--
  One rig, eight states. Everything outside the [ngSwitch] block is constant across
  states — that is what makes the change a crossfade rather than a redraw.
-->
<svg
  class="bb"
  [class.bb--dozing]="state === 'dozing'"
  [class.bb--bob]="state === 'idle'"
  [class.bb--cheer]="state === 'celebratory'"
  [attr.width]="size"
  [attr.height]="size"
  viewBox="0 0 48 48"
  fill="none"
  [attr.role]="decorative ? 'presentation' : 'img'"
  [attr.aria-hidden]="decorative ? 'true' : null"
  [attr.aria-label]="decorative ? null : label"
>
  <!-- Antenna stem + bulb. The bulb is the clearest read on BB's state. -->
  <line x1="24" y1="6" x2="24" y2="13" [attr.stroke]="stemStroke" stroke-width="2" />
  <circle
    class="bb__bulb"
    [class.bb__bulb--pulse]="state === 'listening'"
    cx="24"
    cy="5"
    [attr.r]="state === 'celebratory' ? 3 : 2.6"
    [attr.fill]="accent"
  />
  <!-- Scout and up get the antenna halo (3d). -->
  <circle
    *ngIf="stage !== 'spark' && state !== 'dozing'"
    cx="24"
    cy="5"
    r="5"
    fill="none"
    [attr.stroke]="outline"
    stroke-width="1"
  />

  <!-- Head + visor. -->
  <rect x="7" y="12" width="34" height="27" rx="11" [attr.fill]="headFill" [attr.stroke]="outline" />
  <rect x="11" y="17" width="26" height="15" rx="7.5" [attr.fill]="visorFill" />

  <!-- Only this group changes between states. -->
  <g class="bb__face" [ngSwitch]="state">

    <!-- Idle — round eyes, steady chin bar. -->
    <ng-container *ngSwitchCase="'idle'">
      <circle cx="18" cy="24.5" r="2.8" [attr.fill]="accent" />
      <circle cx="30" cy="24.5" r="2.8" [attr.fill]="accent" />
      <rect x="18" y="35" width="12" height="2.5" rx="1.25" [attr.fill]="chinFill" />
    </ng-container>

    <!-- Listening — wide eyes and a live 4-bar meter for a chin. -->
    <ng-container *ngSwitchCase="'listening'">
      <circle cx="18" cy="24.5" r="3.2" [attr.fill]="accent" />
      <circle cx="30" cy="24.5" r="3.2" [attr.fill]="accent" />
      <rect class="bb__bar" x="15" y="35" width="3" height="4" rx="1.5" [attr.fill]="accent" />
      <rect class="bb__bar bb__bar--2" x="20" y="34" width="3" height="6" rx="1.5" [attr.fill]="accent" />
      <rect class="bb__bar bb__bar--3" x="25" y="34" width="3" height="6" rx="1.5" [attr.fill]="accent" />
      <rect class="bb__bar bb__bar--4" x="30" y="35" width="3" height="4" rx="1.5" [attr.fill]="accent" />
    </ng-container>

    <!-- Thinking — eyes up, three cycling dots. -->
    <ng-container *ngSwitchCase="'thinking'">
      <circle cx="19" cy="23" r="2.4" [attr.fill]="accent" />
      <circle cx="31" cy="23" r="2.4" [attr.fill]="accent" />
      <circle class="bb__dot" cx="15" cy="29" r="1.4" [attr.fill]="accent" />
      <circle class="bb__dot bb__dot--2" cx="20" cy="29" r="1.4" [attr.fill]="accent" />
      <circle class="bb__dot bb__dot--3" cx="25" cy="29" r="1.4" [attr.fill]="accent" />
      <rect x="18" y="35" width="12" height="2.5" rx="1.25" [attr.fill]="chinFill" />
    </ng-container>

    <!-- Speaking — moving mouth plus side arcs. -->
    <ng-container *ngSwitchCase="'speaking'">
      <circle cx="18" cy="24.5" r="2.8" [attr.fill]="accent" />
      <circle cx="30" cy="24.5" r="2.8" [attr.fill]="accent" />
      <rect class="bb__mouth" x="20" y="34" width="8" height="4" rx="2" [attr.fill]="accent" />
      <path class="bb__arc" d="M43 20 q4 4 0 8" [attr.stroke]="accent" stroke-width="1.8" stroke-linecap="round" />
      <path class="bb__arc bb__arc--2" d="M4 21 q-3 3 0 6" [attr.stroke]="accent" stroke-width="1.8" stroke-linecap="round" />
    </ng-container>

    <!-- Happy — the eyes curve up. -->
    <ng-container *ngSwitchCase="'happy'">
      <path d="M15 26.5 q3 -4.5 6 0" [attr.stroke]="accent" stroke-width="2.4" stroke-linecap="round" />
      <path d="M27 26.5 q3 -4.5 6 0" [attr.stroke]="accent" stroke-width="2.4" stroke-linecap="round" />
      <rect x="19" y="35" width="10" height="2.5" rx="1.25" [attr.fill]="chinFill" />
    </ng-container>

    <!-- Celebratory — happy, plus cheeks and two motes. -->
    <ng-container *ngSwitchCase="'celebratory'">
      <path d="M15 26.5 q3 -5 6 0" [attr.stroke]="accent" stroke-width="2.4" stroke-linecap="round" />
      <path d="M27 26.5 q3 -5 6 0" [attr.stroke]="accent" stroke-width="2.4" stroke-linecap="round" />
      <circle cx="20" cy="36" r="1.8" [attr.fill]="accent" />
      <circle cx="28" cy="36" r="1.8" [attr.fill]="accent" />
      <path
        class="bb__mote"
        d="M42 12 l1.6 3.4 3.4 1.6 -3.4 1.6 -1.6 3.4 -1.6 -3.4 -3.4 -1.6 3.4 -1.6z"
        [attr.fill]="accent"
      />
      <path
        class="bb__mote bb__mote--2"
        d="M5 26 l1.2 2.6 2.6 1.2 -2.6 1.2 -1.2 2.6 -1.2 -2.6 -2.6 -1.2 2.6 -1.2z"
        [attr.fill]="moteAlt"
      />
    </ng-container>

    <!-- Oops — crossed eyes and a frown. -->
    <ng-container *ngSwitchCase="'oops'">
      <path d="M16 22.5 l4 4 M20 22.5 l-4 4" [attr.stroke]="accent" stroke-width="2.2" stroke-linecap="round" />
      <path d="M28 22.5 l4 4 M32 22.5 l-4 4" [attr.stroke]="accent" stroke-width="2.2" stroke-linecap="round" />
      <path d="M20 37 q4 -3 8 0" [attr.stroke]="accent" stroke-width="2.2" stroke-linecap="round" />
    </ng-container>

    <!-- Dozing — flat eyes and a pair of z's. -->
    <ng-container *ngSwitchCase="'dozing'">
      <path d="M15 24.5 h6 M27 24.5 h6" [attr.stroke]="restingEye" stroke-width="2.2" stroke-linecap="round" />
      <text x="38" y="16" [attr.fill]="restingEye" class="bb__z">z</text>
      <text x="42" y="10" [attr.fill]="restingEye" class="bb__z bb__z--small">z</text>
    </ng-container>
  </g>
</svg>
